// Library for Events that are strongly typed
// see https://github.com/vitaly-t/sub-events
import { SubEvent } from 'sub-events';
import { LogicEngine } from '../LogicEngine/LogicEngine';
import { DataDictionary } from '../utils/DataDictionary';
import { DataQuery } from '../utils/DataQuery';
import { DataRecord } from '../utils/DataRecord';

import { DataServices } from '../utils/DataServices';
import { Participant } from '../Participant/Participant';

import { TaskConfigDataType } from './TaskTypes';
import { TaskRecordDataType } from './TaskTypes';
import { AuditLog } from '../Participant/AuditLog';
import { RecordCache } from './RecordCache';
import { DataTools } from '../utils/DataTools';


// Cache the participant record for a short amount of time
let cacheParticipantRecord: Participant | undefined;
let cacheParticipantTime: Date | undefined;

let taskCache: RecordCache<DataRecord> = new RecordCache<DataRecord>();

/** Wrapper around a task item that has been parsed from the excel configuration */
export class TaskConfigurationItem
{
	id: string;
	hours_visible: string;
	is_blocking: string;
	title: string;
	subtitle: string;
	task_icon: string;
	task_route: string;


	/**
	 * Use the definition of a task to create a new task record and add it
	 * to the tasks table for the participant.  All tasks use the "TaskCommon" base
	 * element.
	 *
	 * @param uuid The Participant ID
	 */
	async createTaskForParticipant(uuid: string, logic: LogicEngine | undefined, taskName: string | undefined)
	{
		let part = new Participant();

		if (cacheParticipantRecord && uuid != cacheParticipantRecord.participantDataRecord?.data.uuid)
		{
			// cached participant doesn't match requested
			cacheParticipantRecord = undefined;
		}

		// Load the participant and save task count to the logic engine
		if (cacheParticipantRecord && cacheParticipantTime && ((new Date().getTime() - cacheParticipantTime.getTime()) < 500))
		{
			part = cacheParticipantRecord;
			// Brian - Don't update the cache timer on duplicate use of the same participant.
			// cacheParticipantTime = new Date();
		} else
		{
			await part.loadById(uuid);
			cacheParticipantRecord = part;
			cacheParticipantTime = new Date();
		}

		let targetTitle = this.title;

		if (!part.participantDataRecord)
		{
			throw "Error: createTaskForParticipant but participant not loaded."
		}

		// Cleanup Task name
		if (!taskName) taskName = "Unknown Task";
		taskName = taskName.trim();
		taskName = taskName.replace(/    /g, " ");
		taskName = taskName.replace(/   /g, " ");
		taskName = taskName.replace(/  /g, " ");
		taskName = taskName.replace(/  /g, " ");
		taskName = taskName.replace(/  /g, " ");

		// Create the task
		const task: Partial<TaskRecordDataType> = {};
		task.participant_id = part.participantDataRecord.data.uuid;
		task.created_dt = new Date();
		task.created_by = LogicEngine.currentUser;
		task.task_status = 'P';
		task.is_enabled = true;
		task.is_blocking = false;
		task.title_en = this.title;
		task.subtitle_en = this.subtitle;
		task.task_icon = this.task_icon;
		task.task_route = this.task_route;
		task.task_name = this.id;
		task.lock_status = false;

		if (logic)
		{
			task.title_en = logic.processTextReplacement(this.title);
			task.subtitle_en = logic.processTextReplacement(this.subtitle);

			if (taskName)
			{
				targetTitle = logic.processTextReplacement(taskName);
				task.title_en = targetTitle;
			}

			if (LogicEngine.currentSiteId != "")
				task.task_site_id = LogicEngine.currentSiteId;
		}

		try
		{
			task.hours_visible = parseInt(this.hours_visible.toString(), 10);
		} catch
		{
			task.hours_visible = 10000;
		}

		// Search to see if this task already exists
		let searchRecords = taskCache.find({ participant_id: task.participant_id, title_en: task.title_en });
		if (!searchRecords || searchRecords.length == 0)
			searchRecords = await DataQuery.instance().executeQuery("Tasks", `participant_id=${task.participant_id}&title_en=${task.title_en}`);

		if (searchRecords && searchRecords.length > 0)
		{
			console.log("TaskManager createTaskForParticipant duplicate task '", task.title_en, "' found.  Will not create second copy.");

			if (logic)
			{
				logic.assign("create_task_id", searchRecords[0].data.id);
				logic.assign("create_task_route", searchRecords[0].data.task_route);
			} else
			{
				console.log("createTaskForParticipant no logic")
			}

			taskCache.add(searchRecords[0], 1000);

		} else
		{
			// Save the task record
			const dataDictionary = await DataServices.instance().getDataDictionary();
			const record: DataRecord<TaskRecordDataType> = dataDictionary.getRecordFromObject("Tasks", task);
			record.validateId();

			// Fix the Page Route
			record.data.task_route = "/_task" + record.data.id + record.data.task_route;

			await record.fixRecord();
			await record.saveIfNeeded();
			taskCache.add(record, 1000);

			if (logic)
			{
				if (LogicEngine.debugOutput)
					console.log("createTaskForParticipant Last Created Task", record.data.task_route);
				logic.assign("create_task_id", record.data.id);
				logic.assign("create_task_route", record.data.task_route);
			} else
			{
				console.log("createTaskForParticipant no logic")
			}

		}

		if (logic && part.participantDataRecord && part.participantDataRecord.data)
			logic.import(part.participantDataRecord.data);
	}

	constructor(options: TaskConfigDataType)
	{
		this.id = options.id;
		this.hours_visible = options.hours_visible;
		this.is_blocking = options.is_blocking;
		this.title = options.title_en;
		this.subtitle = options.subtitle_en ? options.subtitle_en : "";
		this.task_icon = options.task_icon;
		this.task_route = options.task_route;
	}
};

/** Wrapper around the Tasks data in the database.  Uses DataRecord */
export class TaskDataRecord
{
	record: DataRecord<TaskRecordDataType>;

	/** Update the status of a task, return true if the status was changed or false if
	 * the status was already set to this value.
	 */
	async setStatus(newTaskStatus: string, reason: string): Promise<boolean>
	{
		if (this.record.data.task_status === newTaskStatus)
			return false;

		const oldStatus = this.record.data.task_status;

		if (newTaskStatus === "R")
		{
			this.record.data.is_reviewed = true;
			this.record.data.reviewed_dt = new Date();
			this.record.data.reviewed_by = LogicEngine.currentUser;

			//  Mark as closed as well if not already closed
			if (DataTools.internalValidateString(this.record.data.closed_by) === '')
			{
				this.record.data.closed_dt = new Date();
				this.record.data.closed_by = LogicEngine.currentUser;
				this.record.data.closed_reason = reason;
			}
		}

		/* Closing a task, update the date and user */
		if (newTaskStatus === "C")
		{
			this.record.data.closed_dt = new Date();
			this.record.data.closed_by = LogicEngine.currentUser;
			this.record.data.closed_reason = reason;
		}

		if (newTaskStatus === "E")
		{
			this.record.data.closed_dt = new Date();
			this.record.data.closed_by = LogicEngine.currentUser;
			this.record.data.closed_reason = reason;
		}

		this.record.data.task_status = newTaskStatus;
		await this.record.saveIfNeeded();

		/** Now the task database has been updated.  Time to load the participant for the
		 * task, update the task count value and add an audit record.
		 */
		const participant = new Participant();
		if (await participant.loadById(this.record.data.participant_id))
		{
			// await participant.updateParticipantTaskCount();
			if (participant.participantDataRecord)
			{
				/* Save an audit logic record for this change */
				const audit = new AuditLog(participant.participantDataRecord.data.uuid,
					`Task id ${this.record.data.id} (${this.record.data.task_name}) status changed from '${oldStatus}' to '${newTaskStatus}'. ${reason}`);
				audit.setEventType(AuditLog.EventTypes.StatusChanged);
				audit.setChangeValues(oldStatus, newTaskStatus);
				audit.setTableName(this.record.tableName, this.record.data.id, "status");
				audit.setReasonText(reason);
				await audit.save();
			}

		} else
		{
			console.warn("Attempting to update participant ", this.record.data.participant_id, " in setStatus failed.");
		}

		return true;
	}

	constructor(dataRecord: DataRecord)
	{
		this.record = dataRecord;
	}
}

/**
 * Wrapper around Tasks.  Manages to create, find, close, and otherwise
 * deal with Participant tasks.
 *
*/
export class TaskManager
{
	rawTasks: TaskConfigDataType[] = [];
	taskList: { [taskName: string]: TaskConfigurationItem } = {};

	/**
	 * PageManager is a single instance class pattern and should
	 * be loaded through the instance function.
	 * @example
	 * PageManager.instance()
	 */
	protected static _instance: TaskManager | undefined;

	/** Clear the task cache for all users */
	static clearTaskCache()
	{
		taskCache = new RecordCache<DataRecord>();
	}

	/** Return an instance of the PageManager */
	static instance(): TaskManager
	{
		if (!TaskManager._instance)
		{
			TaskManager._instance = new TaskManager();
		}

		return TaskManager._instance;
	}

	/**
	 * Load datarecord from the Tasks table given a task Id.  This is used
	 * for pending/active tasks from the database.
	 *
	 * @param taskId
	 */
	async getTaskDataById(taskId: string)
	{
		const cache = taskCache.find({ id: taskId });
		if (cache && cache.length > 0)
		{
			return new TaskDataRecord(cache[0]);
		}

		const part = await DataQuery.instance().executeQueryById("Tasks", taskId);
		if (part)
		{
			taskCache.add(part, 1000);
			return new TaskDataRecord(part);
		}


		throw "Attempting to load a task by id " + taskId + " but no data was found.";
	}

	/** Load the data data from the server.  Should not be called directly.
	 *  Use the getTask function instead.
	 */
	async getTaskDataFromServer()
	{
		const dataService = DataServices.instance();
		let data = await dataService.getTaskConfig();
		for (let item of data.tables.Tasks[0].data)
		{
			const task = (item as any) as TaskConfigDataType;
			this.rawTasks.push(task);
			this.taskList[task.id.toLocaleLowerCase()] = new TaskConfigurationItem(task);
		}
	}

	/**
	 * Find a task based on the name of the task
	 * @param taskName
	 */
	async getTask(taskName: string): Promise<TaskConfigurationItem>
	{
		if (this.rawTasks.length === 0)
		{
			await this.getTaskDataFromServer();
		}

		if (this.taskList[taskName.toLocaleLowerCase()])
		{
			return this.taskList[taskName.toLocaleLowerCase()];
		}

		throw "Unable to find task " + taskName + " at TaskManager:getTask()";
	}

	/** Load an existing task from the users list of tasks by title
	@param uuid [string]  Participant ID
	@param taskTitle [string] The task title to search for
	*/
	async loadByTitle(uuid: string, taskTitle: string): Promise<DataRecord<TaskRecordDataType> | undefined>
	{
		if (!taskTitle) taskTitle = "Unknown Task";
		taskTitle = taskTitle.replace(/  /g, " ");
		taskTitle = taskTitle.replace(/  /g, " ");

		// Try to get from the local cache first
		let cachedRecords = taskCache.find({ participant_id: uuid, title_en: taskTitle });
		if (cachedRecords && cachedRecords.length > 0)
		{
			return cachedRecords[0];
		}

		//  Get from the API
		let dq = DataQuery.instance();
		const searchRecords = await DataQuery.instance().executeQuery("Tasks", `participant_id=${uuid}&title_en=${taskTitle}`);
		if (searchRecords && searchRecords.length > 0)
		{
			taskCache.add(searchRecords[0], 1000);
			return searchRecords[0];
		}

		return undefined;
	}

	async loadAllTaskByParticipant(uuid: string): Promise<DataRecord<TaskDataRecord>[] | undefined>
	{
		let dq = DataQuery.instance();
		const searchRecords = await DataQuery.instance().executeQuery("Tasks", `participant_id=${uuid}`);
		console.log("TaskManager loadAllTaskByParticipant Searching for all tasks:", uuid, "found=", searchRecords.length);
		if (searchRecords && searchRecords.length > 0)
		{
			for (const obj of searchRecords)
			{
				taskCache.add(obj, 5000);
			}

			return searchRecords;
		}

		return undefined;
	}

}
